import { type StoryblokAlgoliaCategorySearchFilterConditionType } from '../../storyblok'

import {
  buildDiscountClause,
  buildPriceClause,
  joinWithOr,
  normalizeStringArray,
  normalizeStringList,
  orSameAttribute,
} from './storyblokAlgoliaCategorySearchFiltersText'
import {
  ALGOLIA_ATTRIBUTES,
  type OrGroupPack,
} from './storyblokAlgoliaCategorySearchFiltersTypes'

const DAY_IN_MILLIS = 86_400_000
const DEFAULT_NOVELTY_DAYS = 60
const SPECIAL_PROMOTION_TYPE = {
  NOW_DELIVERY: 'now_delivery',
  REDPOINTS_MULTIPLIER: 'redpoints_multiplier',
} as const

const isSpecialPromotionType = (value: string): boolean =>
  value === SPECIAL_PROMOTION_TYPE.NOW_DELIVERY ||
  value === SPECIAL_PROMOTION_TYPE.REDPOINTS_MULTIPLIER

const buildRedpointsClause = (): string => {
  const now = Date.now()

  return `(${ALGOLIA_ATTRIBUTES.redpointsMultiplier} > 1 AND ${ALGOLIA_ATTRIBUTES.redpointsStartDate} <= ${now} AND ${ALGOLIA_ATTRIBUTES.redpointsEndDate} >= ${now})`
}

const buildPromotionClause = (promotionTypes?: string[]): string => {
  const values = normalizeStringArray(promotionTypes)
  const clauses = [
    orSameAttribute(
      ALGOLIA_ATTRIBUTES.promotionType,
      values.filter((value) => !isSpecialPromotionType(value)),
    ),
  ]

  if (values.includes(SPECIAL_PROMOTION_TYPE.NOW_DELIVERY)) {
    clauses.push(
      orSameAttribute(ALGOLIA_ATTRIBUTES.nowProductGroup, ['sameday_nextday']),
    )
  }
  if (values.includes(SPECIAL_PROMOTION_TYPE.REDPOINTS_MULTIPLIER)) {
    clauses.push(buildRedpointsClause())
  }

  return joinWithOr(clauses)
}

const buildNoveltyClause = (noveltyDays?: string): string => {
  const parsedDays = noveltyDays?.trim()
    ? Number(noveltyDays)
    : DEFAULT_NOVELTY_DAYS
  const days = Number.isFinite(parsedDays) ? parsedDays : DEFAULT_NOVELTY_DAYS
  const cutoff = Date.now() - days * DAY_IN_MILLIS

  return `(${ALGOLIA_ATTRIBUTES.created} >= ${cutoff} AND ${ALGOLIA_ATTRIBUTES.prescriptionMedicine}:false)`
}

export const conditionToClause = (
  condition: StoryblokAlgoliaCategorySearchFilterConditionType,
): string => {
  switch (condition.filterType) {
    case 'Discount Range': {
      return buildDiscountClause(condition.minDiscount, condition.maxDiscount)
    }
    case 'Novelty': {
      return buildNoveltyClause(condition.noveltyDays)
    }
    case 'Price Range': {
      return buildPriceClause(condition.minPrice, condition.maxPrice)
    }
    case 'Promotion Id': {
      return orSameAttribute(
        ALGOLIA_ATTRIBUTES.promotionId,
        normalizeStringList(condition.promotionId),
        '=',
      )
    }
    case 'Promotion Type': {
      return buildPromotionClause(condition.promotionType)
    }
    case 'Seller Id': {
      return orSameAttribute(
        ALGOLIA_ATTRIBUTES.sellerId,
        normalizeStringList(condition.sellerId),
      )
    }
    case 'Seller Promotion Type': {
      return orSameAttribute(
        ALGOLIA_ATTRIBUTES.sellerPromotionType,
        normalizeStringArray(condition.sellerPromotionType),
      )
    }
    case 'Volume Pricing': {
      return condition.volumePricing
        ? `(${ALGOLIA_ATTRIBUTES.volumePricing} > 1)`
        : ''
    }
    default: {
      return ''
    }
  }
}

export const conditionToOrPack = (
  condition: StoryblokAlgoliaCategorySearchFilterConditionType,
): OrGroupPack => {
  const filterType = condition.filterType ?? ''

  if (filterType === 'Seller Id') {
    return {
      attribute: ALGOLIA_ATTRIBUTES.sellerId,
      values: normalizeStringList(condition.sellerId),
    }
  }
  if (filterType === 'Promotion Id') {
    return {
      attribute: ALGOLIA_ATTRIBUTES.promotionId,
      operator: '=',
      values: normalizeStringList(condition.promotionId),
    }
  }
  if (filterType === 'Promotion Type') {
    const values = normalizeStringArray(condition.promotionType)
    if (values.some((value) => isSpecialPromotionType(value))) {
      return null
    }

    return {
      attribute: ALGOLIA_ATTRIBUTES.promotionType,
      values,
    }
  }
  if (filterType === 'Seller Promotion Type') {
    return {
      attribute: ALGOLIA_ATTRIBUTES.sellerPromotionType,
      values: normalizeStringArray(condition.sellerPromotionType),
    }
  }

  return null
}
